import { createContext, ReactNode, useMemo } from "react";

// The lead/application selected in the Details panel. Drives which of the
// contact's own applications the chat-to-SMS is sent about (and thus which of
// the contact's applicant rows — hence phone — receives it), instead of the
// contact's most-recently-updated application where they may be a co-applicant.
export interface InboxSubject {
  type: "lead" | "application";
  id: string;
}

interface InboxContextValue {
  state: {
    selectedConversationId?: string;
    targetMessageId?: string;
    showActivityPanel: boolean;
    showConversationList: boolean;
    initialUnreadCount: number;
    // Client-only state for a "new conversation" that hasn't hit the DB yet.
    // Stays set until the first message is sent (then we swap to
    // selectedConversationId) or the user navigates away.
    draftContactId?: number;
    selectedSubject?: InboxSubject;
  };
  actions: {
    selectConversation: (
      id: string | undefined,
      targetMessageId?: string,
      unreadCount?: number
    ) => void;
    selectDraftContact: (contactId: number) => void;
    clearDraft: () => void;
    clearTargetMessage: () => void;
    toggleList: () => void;
    setShowDetails: (show: boolean) => void;
    setSelectedSubject: (subject?: InboxSubject) => void;
    markConversationRead: (
      conversationId: string,
      trigger: "open" | "reply" | "incoming"
    ) => void;
  };
}

export const InboxContext = createContext<InboxContextValue | undefined>(
  undefined
);

export const InboxProvider = ({
  children,
  value
}: {
  children: ReactNode;
  value: InboxContextValue;
}) => {
  const memoizedValue = useMemo(
    () => ({
      state: value.state,
      actions: value.actions
    }),
    [value.state, value.actions]
  );

  return (
    <InboxContext.Provider value={memoizedValue}>
      {children}
    </InboxContext.Provider>
  );
};
